/**
 * Project stack inventory (G10): each machine that has the project checked out seals one
 * `inventory` entity per project. This merges them for review: newest assertion per key wins,
 * readiness is kept per machine, and brokered secret uses are listed newest first.
 */
import {
  INVENTORY_CATEGORIES,
  type InventoryAccess,
  type InventoryCategory,
  type InventoryEntry,
  type InventoryStatus,
  ProjectInventory,
} from '@viko/protocol'
import type { BrowserEntities } from './encrypted-entities.js'

export interface InventoryView {
  entries: (InventoryEntry & { statuses: InventoryStatus[] })[]
  byCategory: Record<InventoryCategory, InventoryView['entries']>
  access: (InventoryAccess & { machineId?: string })[]
  scannedAt?: number
  machines: string[]
  /** Required entries that are missing on at least one machine. */
  missing: InventoryView['entries']
}

export const CATEGORY_LABEL: Record<InventoryCategory, string> = {
  component: 'Components',
  technology: 'Technologies',
  service: 'Services',
  account: 'Accounts',
  workflow: 'Workflows',
  skill: 'Agent skills',
  tool: 'Tools',
}

export function mergeInventories(values: unknown[], projectId: string): InventoryView {
  const invs = values
    .map((v) => ProjectInventory.safeParse(v))
    .filter((r) => r.success)
    .map((r) => r.data)
    // Authenticated but for another project: ignore.
    .filter((i) => i.projectId === projectId)
  const byKey = new Map<string, InventoryEntry & { statuses: InventoryStatus[] }>()
  const access: InventoryView['access'] = []
  let scannedAt: number | undefined
  const machines = new Set<string>()
  for (const inv of invs) {
    if (inv.machineId) machines.add(inv.machineId)
    if (inv.scannedAt !== undefined) scannedAt = Math.max(scannedAt ?? 0, inv.scannedAt)
    for (const a of inv.access ?? [])
      access.push({ ...a, ...(inv.machineId ? { machineId: inv.machineId } : {}) })
    for (const e of inv.entries) {
      const status = e.status
        ? [
            {
              ...e.status,
              ...(inv.machineId && !e.status.machineId ? { machineId: inv.machineId } : {}),
            },
          ]
        : []
      const prev = byKey.get(e.key)
      if (!prev) {
        byKey.set(e.key, { ...e, statuses: status })
        continue
      }
      // Newest assertion wins field by field; older machines fill what it leaves out.
      const [older, newer] = e.updatedAt > prev.updatedAt ? [prev, e] : [e, prev]
      byKey.set(e.key, {
        ...older,
        ...newer,
        paths: [...new Set([...(prev.paths ?? []), ...(e.paths ?? [])])],
        statuses: [...prev.statuses, ...status],
      })
    }
  }
  const entries = [...byKey.values()].sort((a, b) => a.name.localeCompare(b.name))
  const byCategory = Object.fromEntries(
    INVENTORY_CATEGORIES.map((c) => [c, entries.filter((e) => e.category === c)]),
  ) as InventoryView['byCategory']
  return {
    entries,
    byCategory,
    access: access.sort((a, b) => b.ts - a.ts),
    ...(scannedAt !== undefined ? { scannedAt } : {}),
    machines: [...machines].sort(),
    missing: entries.filter((e) => e.statuses.some((s) => s.state === 'missing')),
  }
}

export async function loadProjectInventory(
  entities: Pick<BrowserEntities, 'list'>,
  projectId: string,
): Promise<InventoryView> {
  const rows = await entities.list('inventory')
  return mergeInventories(
    rows.map((r) => r.value),
    projectId,
  )
}
